import {useCallback, useState} from 'react';
import type {PathNode, ChangePathSource} from '../types/directory';
import {setPathIdsToUrl} from '../utils/path';

export function usePath() {
  const [current, setCurrent] = useState<PathNode | null>(null);
  const [segments, setSegments] = useState<PathNode[]>([]);

  const initRoot = useCallback((root: PathNode) => {
    setCurrent(prev => prev ?? root);
    setSegments(prev => prev.length ? prev : [root]);
  }, []);

  const changePath = useCallback((node: PathNode, source: ChangePathSource = 'list') => {
    setCurrent(node);

    setSegments(prev => {
      let nextSegments: any;

      const existingIndex = prev.findIndex(item => item.id === node.id);

      if (existingIndex >= 0) {
        nextSegments = prev.slice(0, existingIndex + 1);
      } else if (source === 'tree') {
        const root = prev[0];
        nextSegments = root ? [root, node] : [node];
      } else {
        nextSegments = [...prev, node];
      }

      setPathIdsToUrl(
        nextSegments.map((item: any) => item.id)
      );

      return nextSegments;
    });
  }, []);

  const restorePath = useCallback((nextSegments: PathNode[]) => {
    setSegments(prev => {
      const same =
        prev.length === nextSegments.length &&
        prev.every((item, index) =>
          String(item.id) === String(nextSegments[index].id) &&
          item.name === nextSegments[index].name &&
          item.path === nextSegments[index].path
        );

      if (same) return prev;

      return nextSegments;
    });

    setCurrent(prev => {
      const nextCurrent = nextSegments[nextSegments.length - 1] || null;

      if (
        prev &&
        nextCurrent &&
        String(prev.id) === String(nextCurrent.id) &&
        prev.name === nextCurrent.name &&
        prev.path === nextCurrent.path

      ) return prev;

      return nextCurrent;
    });

    setPathIdsToUrl(
      nextSegments.map(item => item.id)
    );
  }, []);

  const setCurrentOnly = useCallback((node: PathNode) => {
    setCurrent(node);
  }, []);

  const resetPath = useCallback(() => {
    setCurrent(null);
    setSegments([]);
    setPathIdsToUrl([]);
  }, []);

  return {
    current,
    segments,
    initRoot,
    changePath,
    restorePath,
    setCurrentOnly,
    resetPath
  };
}